import { cx } from 'flairup'
import * as React from 'react'

import { ClassNames } from '../../DomUtils/classNames'
import { stylesheet } from '../../Stylesheet/stylesheet'
import {
  Categories,
  CustomCategory,
  categoryFromCategoryConfig,
} from '../../config/categoryConfig'
import {
  useCategoriesConfig,
  useEmojiStyleConfig,
  useGetEmojiUrlConfig,
  useLazyLoadEmojisConfig,
  useSkinTonesDisabledConfig,
} from '../../config/useConfig'
import { emojisByCategory } from '../../dataUtils/emojiSelectors'
import { useIsEmojiDisallowed } from '../../hooks/useDisallowedEmojis'
import { useIsEmojiHidden } from '../../hooks/useIsEmojiHidden'
import { ClickableEmoji } from '../emoji/Emoji'
import { EmojiProperties } from '../../dataUtils/DataTypes'
import {
  useActiveSkinToneState,
  useIsPastInitialLoad,
} from '../context/PickerContext'

import { EmojiCategory } from './EmojiCategory'
import { Suggested } from './Suggested'
import { emojiUnified } from '../../dataUtils/emojiUnified'

export function EmojiList() {
  const categories = useCategoriesConfig()
  const renderedCategoriesCountRef = React.useRef(0)

  const customEmojis = emojisByCategory(Categories.CUSTOM)

  const customCategories = customEmojis.reduce(
    (acc, item) => {
      const uniqCategory = `${item[EmojiProperties.category]}`

      acc[uniqCategory] = {
        categoryName: uniqCategory,
        ...(categories.find((c) => c.category === Categories.CUSTOM) || {}),
      } as CustomCategory

      return acc
    },
    {} as Record<string, CustomCategory>,
  )

  const allCategories = [
    categories[0],
    ...Object.values(customCategories),
    ...categories.slice(1),
  ] as CustomCategory[]

  return (
    <ul className={cx(styles.emojiList)}>
      {allCategories.map((categoryConfig, index) => {
        const category = categoryFromCategoryConfig(categoryConfig)

        if (category === Categories.SUGGESTED) {
          return <Suggested key={category} categoryConfig={categoryConfig} />
        }

        return (
          <React.Suspense
            key={`${category}-${categoryConfig.categoryName}-${index}`}
          >
            <RenderCategory
              category={category}
              categoryConfig={categoryConfig}
              renderedCategoriesCountRef={renderedCategoriesCountRef}
            />
          </React.Suspense>
        )
      })}
    </ul>
  )
}

function RenderCategory({
  category,
  categoryConfig,
  renderedCategoriesCountRef,
}: {
  category: Categories
  categoryConfig: CustomCategory
  renderedCategoriesCountRef: React.MutableRefObject<number>
}) {
  const isEmojiHidden = useIsEmojiHidden()
  const lazyLoadEmojis = useLazyLoadEmojisConfig()
  const emojiStyle = useEmojiStyleConfig()
  const isPastInitialLoad = useIsPastInitialLoad()
  const [activeSkinTone] = useActiveSkinToneState()
  const isEmojiDisallowed = useIsEmojiDisallowed()
  const getEmojiUrl = useGetEmojiUrlConfig()
  const showVariations = !useSkinTonesDisabledConfig()

  // Small trick to defer the rendering of all emoji categories until the first category is visible
  // This way the user gets to actually see something and not wait for the whole picker to render.
  const emojisToPush =
    !isPastInitialLoad && renderedCategoriesCountRef.current > 0
      ? []
      : emojisByCategory(category).filter(
          (c) => c[EmojiProperties.category] === categoryConfig.categoryName,
        )

  if (emojisToPush.length > 0) {
    renderedCategoriesCountRef.current++
  }

  let hiddenCounter = 0

  const emojis = emojisToPush.map((emoji) => {
    const unified = emojiUnified(emoji, activeSkinTone)
    const { failedToLoad, filteredOut, hidden } = isEmojiHidden(emoji)

    const isDisallowed = isEmojiDisallowed(emoji)

    if (hidden || isDisallowed) {
      hiddenCounter++
    }

    if (isDisallowed) {
      return null
    }

    return (
      <ClickableEmoji
        showVariations={showVariations}
        key={unified}
        emoji={emoji}
        unified={unified}
        hidden={failedToLoad}
        hiddenOnSearch={filteredOut}
        emojiStyle={emojiStyle}
        lazyLoad={lazyLoadEmojis}
        getEmojiUrl={getEmojiUrl}
      />
    )
  })

  return (
    <EmojiCategory
      categoryConfig={categoryConfig}
      // Indicates that there are no visible emojis
      // Hence, the category should be hidden
      hidden={hiddenCounter === emojis.length}
    >
      {emojis}
    </EmojiCategory>
  )
}

const styles = stylesheet.create({
  emojiList: {
    '.': ClassNames.emojiList,
    listStyle: 'none',
    margin: '0',
    padding: '0',
  },
})
